/**
 * Coups de pouce, Attendus 1230 « développer » et 1231 « factoriser une
 * expression simple » (3e, Calcul littéral, l. 798 ; un fichier gen_1230 à
 * deux jeux de clés). Seconde passe 3e (2026-09-13). Une page STATIQUE
 * par attendu, le MÊME exemple dans les deux sens, l'exemple du
 * programme : 5(x + 4) = 5x + 20.
 *
 * L'image que l'élève a déjà : la distributivité vue en 5e et 4e avec les
 * flèches du facteur vers chaque terme (« le 5 multiplie les deux »), et
 * le rectangle 5 × (x + 4) coupé en deux (fiche 1109 « un produit : un
 * rectangle »). Les flèches suffisent ici : l'automatisme est le geste.
 *   1230 « Le 5 multiplie les deux » : 5(x + 4) = 5 × x + 5 × 4 = 5x + 20
 *     (contre 5x + 4, le 5 qui ne multiplie que le premier).
 *   1231 « Ce que les deux ont en commun » : 5x + 20, 5x = 5 × x et
 *     20 = 5 × 4, le 5 sort devant la parenthèse : 5(x + 4). Vérification :
 *     je redéveloppe, 5 × 4 = 20.
 *
 * SACRIFIÉ : (5 ; 1 ; 4) sous toutes ses formes et lettres (gen_1230,
 * gen1230.self.test.js).
 */
import { svg } from './1054';

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#64748B';

const ligne = (y, contenu, taille = 12, couleur = ENCRE) => `<text x="200" y="${y}" font-size="${taille}" text-anchor="middle" fill="${couleur}">${contenu}</text>`;
// La lettre x en italique et en serif, le signe × droit : dans la police de l'app les deux se
// confondaient (revue prof 2026-09-18, comme la fiche 1229).
const X = '<tspan font-style="italic" font-family="Georgia,serif">x</tspan>';
const gras = (t, couleur = ENCRE) => `<tspan font-weight="bold" fill="${couleur}">${t}</tspan>`;
const arc = (x1, x2, y, couleur) => `<path d="M ${x1} ${y} Q ${(x1 + x2) / 2} ${y - 22} ${x2} ${y}" fill="none" stroke="${couleur}" stroke-width="1.6" marker-end="url(#pte)"/>`;
const DEFS = `<defs><marker id="pte" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto"><path d="M 0 0 L 7 3.5 L 0 7 z" fill="${ORANGE}"/></marker></defs>`;

// ─── 1230 : 5(x + 4) = 5x + 20 ─────────────────────────────────────────────

const SVG_DEVELOPPER = svg(
    '5 facteur de x plus 4. Deux flèches partent du 5 : une vers x, une vers 4. Le 5 multiplie les deux : 5 fois x plus 5 fois 4, égale 5x plus 20. Ce n’est pas 5x plus 4.',
    `${DEFS}
  <!-- 1. l'expression avec les deux flèches -->
  <text x="200" y="70" font-size="26" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${ORANGE}">5</tspan>(<tspan fill="${BLEU}">${X}</tspan> + <tspan fill="${BLEU}">4</tspan>)</text>
  ${arc(170, 194, 50, ORANGE)}
  ${arc(170, 236, 50, ORANGE)}

  <!-- 2. le développement -->
  ${ligne(118, `${gras('5', ORANGE)} × ${gras(X, BLEU)} + ${gras('5', ORANGE)} × ${gras('4', BLEU)}`, 18)}
  ${ligne(154, `= ${gras(`5${X} + 20`)}`, 20)}

  <!-- 3. le geste -->
  ${ligne(192, 'Le 5 multiplie les deux termes de la parenthèse.', 13)}
  ${ligne(214, `Ce n’est pas 5${X} + 4 : le 4 aussi est multiplié par 5.`, 12, GRIS)}`,
    230,
);

// ─── 1231 : 5x + 20 = 5(x + 4) ─────────────────────────────────────────────

const SVG_FACTORISER = svg(
    '5x plus 20. 5x, c’est 5 fois x. 20, c’est 5 fois 4. Les deux ont 5 en commun : le 5 sort devant la parenthèse, il reste x plus 4. 5x plus 20 égale 5 facteur de x plus 4. Je vérifie en redéveloppant : 5 fois 4 égale 20.',
    `<!-- 1. l'expression, chaque terme écrit en produit -->
  <text x="200" y="46" font-size="26" font-weight="bold" text-anchor="middle" fill="${ENCRE}">5${X} + 20</text>
  ${ligne(86, `${gras('5', ORANGE)} × ${gras(X, BLEU)} + ${gras('5', ORANGE)} × ${gras('4', BLEU)}`, 18)}
  ${ligne(106, 'les deux ont 5 en commun', 11, GRIS)}

  <!-- 2. le 5 sort devant -->
  <text x="200" y="150" font-size="26" font-weight="bold" text-anchor="middle" fill="${ENCRE}">= <tspan fill="${ORANGE}">5</tspan>(<tspan fill="${BLEU}">${X}</tspan> + <tspan fill="${BLEU}">4</tspan>)</text>

  <!-- 3. le geste -->
  ${ligne(192, 'Je cherche ce que les deux termes ont en commun : le 5.', 13)}
  ${ligne(214, 'Il sort devant la parenthèse. Je vérifie : 5 × 4 = 20.', 12, GRIS)}`,
    230,
);

const AIDE_DEVELOPPER = { pages: [{ titre: 'Le 5 multiplie les deux', svg: SVG_DEVELOPPER }] };
const AIDE_FACTORISER = { pages: [{ titre: 'Ce que les deux ont en commun', svg: SVG_FACTORISER }] };

const CP_1230 = {
    developper_flash_texte_trou_saisie: AIDE_DEVELOPPER,
    developper_saisie: AIDE_DEVELOPPER,
    developper_qcm_unique: AIDE_DEVELOPPER,
    developper_tableau_vf: AIDE_DEVELOPPER,
};
/** 1231 : les clés factoriser_* de gen_1230, servies par gen_1231. */
export const CP_1231 = {
    factoriser_flash_texte_trou_saisie: AIDE_FACTORISER,
    factoriser_saisie: AIDE_FACTORISER,
    factoriser_qcm_unique: AIDE_FACTORISER,
    factoriser_tableau_vf: AIDE_FACTORISER,
};

export default CP_1230;
